import { makeAutoObservable } from 'mobx';
import { InstalledEnrollmentType } from 'types/enrollmentService';
import { BiGroup } from 'types/groupService';
import { ManageExhibitsModalDelegate } from './modals/manageExhibitsModalDelegate';
import { ManageEmailsModalDelegate } from './modals/manageEmailsModalDelegate';
import { ChangeEffectiveDateModalDelegate } from './modals/changeEffectiveDateModalDelegate';
import { RemovePeriodModalDelegate } from './modals/removePeriodModalDelegate';
import { MoveToRenewalModalDelegate } from './modals/moveToRenewalModalDelegate';
import { EnrollmentDelegate } from './enrollmentDelegate';

export interface GroupEnrollmentActionContext {
  group: BiGroup;
  enrollmentDelegate: EnrollmentDelegate;
}

interface RenewalGroupActionDelegateParams {
  updateGroup: (updatedGroup: BiGroup) => BiGroup | undefined;
  updateEnrollmentPeriod: (updatedPeriod: InstalledEnrollmentType) => void;
  removeEnrollmentPeriod: (periodId: string, groupId: string) => void;
  getUnavailableEffectiveDates: (groupId: string, periodId: string) => string[];
}

export class RenewalGroupActionDelegate {
  public readonly manageExhibitsModalDelegate: ManageExhibitsModalDelegate;
  public readonly manageEmailsModalDelegate: ManageEmailsModalDelegate;
  public readonly changeEffectiveDateModalDelegate: ChangeEffectiveDateModalDelegate;
  public readonly removePeriodModalDelegate: RemovePeriodModalDelegate;
  public readonly moveToRenewalModalDelegate: MoveToRenewalModalDelegate;
  private readonly _updateGroup: (updatedGroup: BiGroup) => BiGroup | undefined;
  private readonly _updateEnrollmentPeriod: (updatedPeriod: InstalledEnrollmentType) => void;
  private readonly _getUnavailableEffectiveDates: (groupId: string, periodId: string) => string[];
  private _activeContext: GroupEnrollmentActionContext | null = null;

  public get activeContext(): GroupEnrollmentActionContext | null {
    return this._activeContext;
  }

  public setActiveContext(context: GroupEnrollmentActionContext): void {
    this._activeContext = context;
  }

  public openManageExhibits(): void {
    if (!this._activeContext) {
      throw new Error('Tried to open manage exhibits modal without an active context!');
    }

    this.manageExhibitsModalDelegate.open({
      periodId: this._activeContext.enrollmentDelegate.period.id,
      groupName: this._activeContext.group.name,
      effectiveDate: this._activeContext.enrollmentDelegate.period.effectiveDate,
      productLines: this._activeContext.enrollmentDelegate.exhibitProductLines,
    });
  }

  public openManageEmails(): void {
    if (!this._activeContext) {
      throw new Error('Tried to open manage emails modal without an active context!');
    }

    const { emailDelegate } = this._activeContext.enrollmentDelegate;
    this.manageEmailsModalDelegate.open(this._activeContext, emailDelegate.emails, emailDelegate.markEmailsAsSent);
  }

  public openChangeEffectiveDate(): void {
    if (!this._activeContext) {
      throw new Error('Tried to open change effective date modal without an active context!');
    }

    const { group, enrollmentDelegate } = this._activeContext;
    this.changeEffectiveDateModalDelegate.open({
      periodId: enrollmentDelegate.period.id,
      groupId: group.id,
      groupName: group.name,
      effectiveDate: enrollmentDelegate.period.effectiveDate,
      productLineIds: enrollmentDelegate.period.productLineIds,
      unavailableEffectiveDates: this._getUnavailableEffectiveDates(group.id, enrollmentDelegate.period.id),
    });
  }

  public openRemovePeriod(): void {
    if (!this._activeContext) {
      throw new Error('Tried to open remove period modal without an active context!');
    }

    this.removePeriodModalDelegate.open({
      period: this._activeContext.enrollmentDelegate.period,
      groupId: this._activeContext.group.id,
      groupName: this._activeContext.group.name,
    });
  }

  public openMoveToRenewal(): void {
    if (!this._activeContext) {
      throw new Error('Tried to open move to renewal modal without an active context!');
    }

    const { enrollmentDelegate } = this._activeContext;
    this.moveToRenewalModalDelegate.open({
      periodId: enrollmentDelegate.period.id,
      emailReceivers: enrollmentDelegate.emailDelegate.emails.map((email) => ({
        email: email.emailAddress,
        userType: email.userType,
      })),
    });
  }

  public updateEnrollmentPeriod(updatedPeriod: InstalledEnrollmentType): void {
    this._updateEnrollmentPeriod(updatedPeriod);
  }

  public updateGroup(updatedGroup: BiGroup): void {
    const mergedGroup = this._updateGroup(updatedGroup);

    if (mergedGroup && this._activeContext?.group.id === mergedGroup.id) {
      this._activeContext.group = mergedGroup;
    }
  }

  constructor({
    updateGroup,
    updateEnrollmentPeriod,
    removeEnrollmentPeriod,
    getUnavailableEffectiveDates,
  }: RenewalGroupActionDelegateParams) {
    this._updateGroup = updateGroup;
    this._updateEnrollmentPeriod = updateEnrollmentPeriod;
    this._getUnavailableEffectiveDates = getUnavailableEffectiveDates;
    this.manageExhibitsModalDelegate = new ManageExhibitsModalDelegate({
      onEnrollmentPeriodUpdated: (updatedPeriod: InstalledEnrollmentType): void =>
        this.updateEnrollmentPeriod(updatedPeriod),
    });
    this.manageEmailsModalDelegate = new ManageEmailsModalDelegate({
      onGroupUpdated: (updatedGroup: BiGroup): void => this.updateGroup(updatedGroup),
    });
    this.changeEffectiveDateModalDelegate = new ChangeEffectiveDateModalDelegate({
      onEnrollmentPeriodUpdated: (updatedPeriod: InstalledEnrollmentType): void =>
        this.updateEnrollmentPeriod(updatedPeriod),
    });
    this.removePeriodModalDelegate = new RemovePeriodModalDelegate({
      onPeriodRemoved: removeEnrollmentPeriod,
    });
    this.moveToRenewalModalDelegate = new MoveToRenewalModalDelegate({
      onEnrollmentPeriodUpdated: (updatedPeriod: InstalledEnrollmentType): void =>
        this.updateEnrollmentPeriod(updatedPeriod),
    });
    makeAutoObservable(
      this,
      {
        manageExhibitsModalDelegate: false,
        manageEmailsModalDelegate: false,
        changeEffectiveDateModalDelegate: false,
        removePeriodModalDelegate: false,
        moveToRenewalModalDelegate: false,
      },
      { autoBind: true },
    );
  }
}
